import { type FormEvent, useEffect, useState } from "react";
import { AdminPageHeader } from "../../components/admin/AdminPageHeader";
import { AdminPanel } from "../../components/admin/AdminPanel";
import { Badge } from "../../components/ui/Badge";
import { Button } from "../../components/ui/Button";
import { EmptyState } from "../../components/ui/EmptyState";
import { FullPageLoader } from "../../components/ui/FullPageLoader";
import { Input } from "../../components/ui/Input";
import { useTenant } from "../../hooks/useTenant";
import { formatDate, statusTone } from "../../lib/format";
import { mealPlanService } from "../../services/mealPlanService";
import type { MealPlan } from "../../types/coaching";

const initialForm = {
  title: "",
  description: "",
  calories: "",
  proteinGrams: "",
  carbsGrams: "",
  fatGrams: "",
  dayTitle: "",
  mealName: "",
  foodDetails: "",
  notes: "",
};

export function TrainerMealsPage() {
  const { currentTenant } = useTenant();
  const [plans, setPlans] = useState<MealPlan[]>([]);
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  async function loadPlans() {
    if (!currentTenant?.id) return;
    setLoading(true);
    setError(null);
    try {
      setPlans(await mealPlanService.listMealPlansForTrainer(currentTenant.id));
    } catch (caughtError) {
      console.error("Unable to load meal plans", caughtError);
      setError("Unable to load meal plans.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void loadPlans();
  }, [currentTenant?.id]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!currentTenant?.id || !form.title.trim()) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await mealPlanService.createMealPlan({
        tenantId: currentTenant.id,
        ...form,
        isTemplate: true,
      });
      setForm(initialForm);
      setSuccess("Meal plan created.");
      await loadPlans();
    } catch (caughtError) {
      console.error("Unable to create meal plan", caughtError);
      setError("Unable to create meal plan.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div>
      <AdminPageHeader
        eyebrow="Coaching content"
        title="Meal plans"
        description="Create simple meal plan templates and assign them from each client profile."
      />

      <div className="grid gap-6 xl:grid-cols-[420px_1fr]">
        <AdminPanel title="Create meal plan" description="V1 supports a simple first day and meal item.">
          <form className="space-y-4" onSubmit={handleSubmit}>
            <Input label="Plan title" value={form.title} onChange={(event) => setForm({ ...form, title: event.target.value })} required />
            <Input label="Description" value={form.description} onChange={(event) => setForm({ ...form, description: event.target.value })} />
            <div className="grid gap-3 sm:grid-cols-2">
              <Input label="Calories" inputMode="numeric" value={form.calories} onChange={(event) => setForm({ ...form, calories: event.target.value })} />
              <Input label="Protein grams" inputMode="numeric" value={form.proteinGrams} onChange={(event) => setForm({ ...form, proteinGrams: event.target.value })} />
              <Input label="Carbs grams" inputMode="numeric" value={form.carbsGrams} onChange={(event) => setForm({ ...form, carbsGrams: event.target.value })} />
              <Input label="Fat grams" inputMode="numeric" value={form.fatGrams} onChange={(event) => setForm({ ...form, fatGrams: event.target.value })} />
            </div>
            <Input label="Day title" value={form.dayTitle} onChange={(event) => setForm({ ...form, dayTitle: event.target.value })} placeholder="Day 1" />
            <Input label="Meal name" value={form.mealName} onChange={(event) => setForm({ ...form, mealName: event.target.value })} placeholder="Breakfast" />
            <label className="block text-sm font-medium text-ink">
              Food details
              <textarea className="mt-2 min-h-24 w-full rounded-md border border-line bg-field px-3.5 py-2.5 text-base text-ink outline-none focus:border-ink focus:ring-2 focus:ring-ink/10" value={form.foodDetails} onChange={(event) => setForm({ ...form, foodDetails: event.target.value })} />
            </label>
            <label className="block text-sm font-medium text-ink">
              Notes
              <textarea className="mt-2 min-h-20 w-full rounded-md border border-line bg-field px-3.5 py-2.5 text-base text-ink outline-none focus:border-ink focus:ring-2 focus:ring-ink/10" value={form.notes} onChange={(event) => setForm({ ...form, notes: event.target.value })} />
            </label>
            {error ? <p className="text-sm font-medium text-red-800">{error}</p> : null}
            {success ? <p className="text-sm font-medium text-pine">{success}</p> : null}
            <Button isLoading={saving} type="submit">Create template</Button>
          </form>
        </AdminPanel>

        <AdminPanel title="Meal plan library">
          {loading ? <FullPageLoader label="Loading meal plans" /> : null}
          {!loading && !plans.length ? (
            <EmptyState title="No meal plans yet.">Create one first, then assign it from a client profile.</EmptyState>
          ) : null}
          <div className="space-y-3">
            {plans.map((plan) => (
              <div className="rounded-md border border-line bg-white p-4" key={plan.id}>
                <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <h2 className="font-semibold text-ink">{plan.title}</h2>
                    <p className="mt-1 text-sm text-graphite/70">{plan.description || "No description."}</p>
                    <p className="mt-2 text-xs text-graphite/55">
                      {plan.client_id ? `Assigned ${formatDate(plan.assigned_at)}` : "Template/unassigned"}
                    </p>
                  </div>
                  <Badge tone={statusTone(plan.status)}>{plan.status}</Badge>
                </div>
                <p className="mt-3 text-sm text-graphite/70">
                  {plan.target_calories ? `${plan.target_calories} kcal` : "No calorie target"}
                  {plan.target_protein_grams ? `, ${plan.target_protein_grams}g protein` : ""}
                </p>
                <div className="mt-3 space-y-2">
                  {(plan.meal_plan_days ?? []).map((day) => (
                    <div className="rounded-md bg-field p-3 text-sm" key={day.id}>
                      <p className="font-medium text-ink">{day.title}</p>
                      {(day.meal_plan_items ?? []).map((item) => (
                        <p className="mt-1 text-graphite/70" key={item.id}>
                          {item.meal_name}: {item.food_details}
                        </p>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </AdminPanel>
      </div>
    </div>
  );
}
